// Story 011 — Filmstrip widget body (W9). Renders one thumb per source
// image, in capture order, as a single-select listbox. Publishes the
// active imageId via imageActivationStore so the Viewer and Tiles can
// subscribe.
//
// SB6-ANA-FILT-05 (Track C+D) — the Filmstrip now reduces to the shared
// filtered DATA-POINT set (`useFilteredDataPoints`), not a client-side
// defect projection. Additions:
//   * Red "INACTIVE" chip overlay on tiles whose data point is INACTIVE.
//   * Multiselect: Ctrl+Click toggles; Shift+Click selects a range.
//     Single-click stays activate/navigate.
//   * Top-of-widget bulk Activate / Deactivate buttons → bulk PATCH
//     `data-points/status`. Activate shows when ≥1 selected DP is
//     INACTIVE; Deactivate when ≥1 is ACTIVE.
//   * Gate: bulk buttons disabled with a tooltip when the DA is LOCKED
//     (read/filter still work in LOCK — D12).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ImageRef } from "@aeriosiq/types";
import { useImagesQuery } from "../../queries/useImagesQuery.js";
import { useImageActivationStore } from "../../state/imageActivationStore.js";
import { ANALYSIS_THUMB_RADIUS } from "../../AnalysisLayout.js";
import { useFilteredDataPoints } from "../../useFilteredDataPoints.js";
import {
  useDataAcquisitionDetailQuery,
  useUpdateDataPointStatusMutation,
} from "../../queries.js";

const THUMB_W = 188;
const THUMB_H = 128;

interface FilmstripWidgetProps {
  dataAcquisitionId: string;
}

export function FilmstripWidget({
  dataAcquisitionId,
}: FilmstripWidgetProps): React.ReactElement {
  const query = useImagesQuery(dataAcquisitionId);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const setActive = useImageActivationStore((s) => s.setActive);

  const filtered = useFilteredDataPoints(dataAcquisitionId);
  const detailQuery = useDataAcquisitionDetailQuery(dataAcquisitionId);
  const isLocked = detailQuery.data?.state === "LOCKED";
  const statusMutation = useUpdateDataPointStatusMutation(dataAcquisitionId);

  // Multiselect state — set of selected imageIds.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);

  const allImages = useMemo<ImageRef[]>(
    () => query.data?.items ?? [],
    [query.data],
  );

  // Reduce to the filtered data-point set: keep only images whose
  // vis-image id is in the filtered set. While the filtered query is in
  // flight we show all images (avoids a flash of empty).
  const items = useMemo<ImageRef[]>(() => {
    if (filtered.isPending) return allImages;
    return allImages.filter((img) => filtered.visibleImageIds.has(img.imageId));
  }, [allImages, filtered.visibleImageIds, filtered.isPending]);

  // Drop selections that are no longer visible.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(items.map((i) => i.imageId));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [items]);

  useEffect(() => {
    if (items.length === 0) return;
    const first = items[0];
    if (!first) return;
    if (activeImageId === null) {
      setActive(first.imageId);
      return;
    }
    const stillVisible = items.some((it) => it.imageId === activeImageId);
    if (!stillVisible) setActive(first.imageId);
  }, [activeImageId, items, setActive]);

  // Click semantics: Ctrl/Cmd+Click toggles; Shift+Click range-selects
  // from the last-clicked anchor; plain click = single activate.
  const handleTileClick = useCallback(
    (
      imageId: string,
      evt: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean },
    ) => {
      const ctrl = evt.ctrlKey || evt.metaKey;
      if (ctrl) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(imageId)) next.delete(imageId);
          else next.add(imageId);
          return next;
        });
        lastClickedRef.current = imageId;
        return;
      }
      if (evt.shiftKey && lastClickedRef.current) {
        const ids = items.map((i) => i.imageId);
        const a = ids.indexOf(lastClickedRef.current);
        const b = ids.indexOf(imageId);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          setSelectedIds(() => new Set(ids.slice(lo, hi + 1)));
          return;
        }
      }
      // Plain click — activate/navigate (and reset multiselect to none).
      setSelectedIds(new Set());
      lastClickedRef.current = imageId;
      setActive(imageId);
    },
    [items, setActive],
  );

  // Bulk-button visibility derives from the statuses of selected DPs.
  const { hasInactiveSelected, hasActiveSelected } = useMemo(() => {
    let inactive = false;
    let active = false;
    for (const id of selectedIds) {
      const s = filtered.statusByImageId.get(id);
      if (s === "INACTIVE") inactive = true;
      else if (s === "ACTIVE") active = true;
    }
    return { hasInactiveSelected: inactive, hasActiveSelected: active };
  }, [selectedIds, filtered.statusByImageId]);

  const runMutation = useCallback(
    (status: "ACTIVE" | "INACTIVE") => {
      const dataPointIds: string[] = [];
      for (const id of selectedIds) {
        const dpId = filtered.dataPointIdByImageId.get(id);
        if (dpId) dataPointIds.push(dpId);
      }
      if (dataPointIds.length === 0) return;
      statusMutation.mutate(
        { dataPointIds, status },
        {
          onSuccess: () => {
            setSelectedIds(new Set());
          },
        },
      );
    },
    [selectedIds, filtered.dataPointIdByImageId, statusMutation],
  );

  if (query.isPending) {
    return <FilmstripSkeleton />;
  }
  if (query.isError) {
    const errorId =
      (query.error as { errorId?: string } | undefined)?.errorId ?? "unknown";
    return (
      <FilmstripErrorPanel
        errorId={errorId}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const showBulkBar = selectedIds.size > 0;
  const lockTooltip =
    "Status changes are disabled while the data acquisition is locked.";

  return (
    <Stack sx={{ height: "100%", minHeight: 0 }} data-testid="filmstrip-widget">
      {showBulkBar ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          data-testid="filmstrip-bulk-bar"
          sx={{
            px: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {selectedIds.size} selected
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {hasInactiveSelected ? (
            <Tooltip
              title={isLocked ? lockTooltip : ""}
              disableHoverListener={!isLocked}
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  data-testid="filmstrip-activate"
                  disabled={isLocked || statusMutation.isPending}
                  onClick={() => {
                    runMutation("ACTIVE");
                  }}
                >
                  Activate
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {hasActiveSelected ? (
            <Tooltip
              title={isLocked ? lockTooltip : ""}
              disableHoverListener={!isLocked}
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  data-testid="filmstrip-deactivate"
                  disabled={isLocked || statusMutation.isPending}
                  onClick={() => {
                    runMutation("INACTIVE");
                  }}
                >
                  Deactivate
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}
      {items.length === 0 ? (
        <FilmstripEmptyPanel />
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <FilmstripList
            items={items}
            activeImageId={activeImageId}
            selectedIds={selectedIds}
            statusByImageId={filtered.statusByImageId}
            onTileClick={handleTileClick}
          />
        </Box>
      )}
    </Stack>
  );
}

function FilmstripSkeleton(): React.ReactElement {
  return (
    <Stack spacing={1} sx={{ p: 1, alignItems: "center" }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          width={THUMB_W}
          height={THUMB_H}
          sx={{ borderRadius: `${String(ANALYSIS_THUMB_RADIUS)}px` }}
        />
      ))}
    </Stack>
  );
}

function FilmstripEmptyPanel(): React.ReactElement {
  return (
    <Box
      role="status"
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        No images for this inspection
      </Typography>
    </Box>
  );
}

interface FilmstripErrorPanelProps {
  errorId: string;
  onRetry: () => void;
}

function FilmstripErrorPanel({
  errorId,
  onRetry,
}: FilmstripErrorPanelProps): React.ReactElement {
  return (
    <Stack
      spacing={1}
      role="alert"
      aria-live="assertive"
      sx={{ p: 2, alignItems: "center", textAlign: "center" }}
    >
      <Typography variant="subtitle2">Couldn&apos;t load images</Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: "monospace" }}
      >
        {errorId}
      </Typography>
      <Button size="small" variant="outlined" onClick={onRetry}>
        Retry
      </Button>
    </Stack>
  );
}

interface FilmstripListProps {
  items: ImageRef[];
  activeImageId: string | null;
  selectedIds: Set<string>;
  statusByImageId: Map<string, "ACTIVE" | "INACTIVE">;
  onTileClick: (
    imageId: string,
    evt: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean },
  ) => void;
}

function FilmstripList({
  items,
  activeImageId,
  selectedIds,
  statusByImageId,
  onTileClick,
}: FilmstripListProps): React.ReactElement {
  const listRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (!activeImageId) return;
    const el = optionRefs.current.get(activeImageId);
    if (!el) return;
    el.scrollIntoView({ block: "nearest", behavior: "auto" });
  }, [activeImageId]);

  const activeIndex = activeImageId
    ? items.findIndex((it) => it.imageId === activeImageId)
    : -1;

  const focusByIndex = (idx: number): void => {
    if (idx < 0 || idx >= items.length) return;
    const next = items[idx];
    if (!next) return;
    const el = optionRefs.current.get(next.imageId);
    el?.focus();
    onTileClick(next.imageId, {
      ctrlKey: false,
      metaKey: false,
      shiftKey: false,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    switch (event.key) {
      // ANA-R3-NAV-01 — the strip is vertical, so Down/Right both go to the
      // next data point and Up/Left both go to the previous one. Left/Right
      // are a convenience alias for Up/Down (Christian, 2026-06-09).
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        focusByIndex(activeIndex + 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        focusByIndex(activeIndex - 1);
        break;
      case "Home":
        event.preventDefault();
        focusByIndex(0);
        break;
      case "End":
        event.preventDefault();
        focusByIndex(items.length - 1);
        break;
      default:
        return;
    }
  };

  return (
    <Box
      ref={listRef}
      role="listbox"
      aria-label="Inspection images"
      aria-multiselectable="true"
      onKeyDown={handleKeyDown}
      sx={{
        height: "100%",
        overflowY: "auto",
        p: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      {items.map((it) => {
        const isActive = it.imageId === activeImageId;
        const isSelected = selectedIds.has(it.imageId);
        const isInactive = statusByImageId.get(it.imageId) === "INACTIVE";
        return (
          <Box
            key={it.imageId}
            component="button"
            ref={(el: HTMLButtonElement | null) => {
              if (el) optionRefs.current.set(it.imageId, el);
              else optionRefs.current.delete(it.imageId);
            }}
            role="option"
            aria-current={isActive ? "true" : "false"}
            aria-selected={isSelected}
            aria-label={`Load image ${it.basename}${isInactive ? " (inactive)" : ""}`}
            tabIndex={isActive ? 0 : -1}
            onClick={(e) => {
              onTileClick(it.imageId, {
                ctrlKey: e.ctrlKey,
                metaKey: e.metaKey,
                shiftKey: e.shiftKey,
              });
            }}
            sx={{
              position: "relative",
              width: THUMB_W,
              height: THUMB_H,
              p: 0,
              border: "none",
              borderRadius: `${String(ANALYSIS_THUMB_RADIUS)}px`,
              cursor: "pointer",
              outline: isSelected
                ? (theme) => `3px solid ${theme.palette.primary.light}`
                : isActive
                  ? (theme) => `2px solid ${theme.palette.primary.main}`
                  : "none",
              outlineOffset: -2,
              backgroundColor: "background.paper",
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              },
              "&:hover": {
                outline: (theme) => `1px solid ${theme.palette.primary.light}`,
              },
            }}
          >
            <Box
              component="img"
              src={it.urls.thumb900}
              alt=""
              loading="lazy"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: `${String(ANALYSIS_THUMB_RADIUS)}px`,
                display: "block",
                filter: isInactive ? "grayscale(0.6) brightness(0.7)" : "none",
              }}
            />
            {isInactive && (
              <Box
                data-testid="filmstrip-inactive-chip"
                sx={(theme) => ({
                  position: "absolute",
                  top: 6,
                  left: 6,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.75,
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                })}
              >
                Inactive
              </Box>
            )}
          </Box>
        );
      })}
      <Box sx={{ display: "none" }}>
        <CircularProgress size={16} aria-hidden="true" />
      </Box>
    </Box>
  );
}
